import { Injectable } from '@angular/core';
import { catchError, filter, first, map, switchMap, take, tap } from 'rxjs/operators';
import { BehaviorSubject, Observable, combineLatest, forkJoin, of, timer } from 'rxjs';

import { DocumentCreateModel, DocumentModel } from '@data-layer/models/documents';
import { DocumentsManagementRepository, DocumentsRepository } from '@data-layer/repositories';
import {
  enumDocumentRelatedEntityType,
  enumDocumentRootEntityType,
  enumDocumentState,
  enumDocumentType,
} from '@data-layer/enums/documents';
import { CompanyModel } from '@data-layer/models/companies';

import { CompaniesService } from './companies.service';
import { LhcbMigrationPopupService } from './lhcb-migration-popup.service';
import { AlertService } from './alert.service';
import { ManagementDocumentModel, ManagementDocumentSearchModel } from '@data-layer/models/management';
import { PaginatedResultModel } from '@data-layer/models/common';
import { DEFAULT_MAX_POLLING_RETRY, DEFAULT_POLLING_TIMEOUT } from '@constants/constants';
import { enumFileExtension } from '@enum/enumFileExtension';
import { DigitalSignsRepository } from '@data-layer/repositories/digital-signs.repository';
import { enumDigitalSignState } from '@data-layer/enums/digital-signs/enumDigitalSignState';

@Injectable({
  providedIn: 'root',
})
export class DocumentsService {
  public isUploadingDocIdsToPush$: BehaviorSubject<string> = new BehaviorSubject(null);
  public isUploadingDocIdsToUnshift$: BehaviorSubject<string> = new BehaviorSubject(null);

  constructor(
    private documentsRepository: DocumentsRepository,
    private documentsManagementService: DocumentsManagementRepository,
    private alertService: AlertService,
    private companiesService: CompaniesService,
    private digitalSignsRepository: DigitalSignsRepository,
    private lhcbMigrationPopupService: LhcbMigrationPopupService,
  ) {}

  public deleteDocument(documentId: number, isShowLoader: boolean): Observable<never> {
    return this.documentsRepository.delete(documentId, isShowLoader);
  }

  public uploadDocument(document: DocumentCreateModel, file: File, companyId?: number, isShowLoader: boolean = false): Observable<number> {
    const fakeDocHash: string = new Date().toLocaleDateString();
    this.isUploadingDocIdsToPush$.next(fakeDocHash);

    return this.documentsRepository
      .create(companyId || this.companiesService.getCompany().id, { ...document, fileName: file.name }, isShowLoader)
      .pipe(
        switchMap(({ id }: { id: number }) =>
          this.documentsRepository.uploadFile(id, file, isShowLoader).pipe(
            map(() => id),
            tap(() => this.isUploadingDocIdsToUnshift$.next(fakeDocHash)),
            catchError(() => {
              return this.documentsRepository.delete(id, isShowLoader);
            }),
          ),
        ),
      );
  }

  public uploadManagementDocument(document: DocumentCreateModel, file: File, isShowLoader: boolean = false): Observable<number> {
    return this.documentsManagementService.create({ ...document, fileName: file.name }, isShowLoader).pipe(
      switchMap(({ id }: { id: number }) =>
        this.documentsRepository.uploadFile(id, file, isShowLoader).pipe(
          map(() => id),
          catchError(() => {
            this.alertService.error('DOCUMENTS.ERROR_UPLOAD_DOCUMENT');
            return this.documentsRepository.delete(id, isShowLoader);
          }),
        ),
      ),
    );
  }

  public removeDocument(documentId: number, isShowLoader: boolean = false): Observable<never> {
    return this.documentsRepository.delete(documentId, isShowLoader);
  }

  public copyPoaToCompany(documentId: number, fileName: string, isTrucker: boolean): Observable<DocumentModel[]> {
    const docType: enumDocumentType = isTrucker ? enumDocumentType.POWER_OF_ATTORNEY_TRUCKER : enumDocumentType.POWER_OF_ATTORNEY;

    return this.findCompanyDocument(docType).pipe(
      switchMap((poaDocument: DocumentModel) => {
        if (poaDocument) {
          return this.documentsRepository.delete(poaDocument.id);
        }
        return of(null);
      }),
      switchMap(() =>
        timer(0, DEFAULT_POLLING_TIMEOUT).pipe(
          take(DEFAULT_MAX_POLLING_RETRY),
          switchMap(() => this.documentsRepository.getDocumentState(documentId, true)),
          filter((state: enumDocumentState) => state !== enumDocumentState.SCAN_PENDING),
          first(),
        ),
      ),
      switchMap(() => {
        return this.documentsRepository.copy(
          documentId,
          {
            type: docType,
            fileName,
            rootEntityId: this.companiesService.getCompany().id,
            rootEntityType: enumDocumentRootEntityType.COMPANY,
            relatedEntityId: this.companiesService.getCompany().id,
            relatedEntityType: enumDocumentRelatedEntityType.COMPANY,
          },
          false,
        );
      }),
    );
  }

  public copyPunToCompany(documentId: number, fileName: string): Observable<DocumentModel[]> {
    return this.findCompanyDocument(enumDocumentType.ACH_APPROVAL).pipe(
      switchMap((poaDocument: DocumentModel) => {
        if (poaDocument) {
          return this.documentsRepository.delete(poaDocument.id);
        }
        return of(null);
      }),
      switchMap(() => {
        return this.documentsRepository.copy(
          documentId,
          {
            type: enumDocumentType.ACH_APPROVAL,
            fileName,
            rootEntityId: this.companiesService.getCompany().id,
            rootEntityType: enumDocumentRootEntityType.COMPANY,
            relatedEntityId: this.companiesService.getCompany().id,
            relatedEntityType: enumDocumentRelatedEntityType.COMPANY,
          },
          false,
        );
      }),
    );
  }

  public copyCompanyPoaToEntry(
    entryId: number,
    docType: enumDocumentType = enumDocumentType.POWER_OF_ATTORNEY,
  ): Observable<DocumentModel[]> {
    return this.findCompanyDocument(docType).pipe(
      switchMap((poaDocument: DocumentModel) => {
        if (!poaDocument || poaDocument.state !== enumDocumentState.UPLOADED) {
          return of(null);
        }
        return this.documentsRepository.copy(
          poaDocument.id,
          {
            ...poaDocument,
            rootEntityId: entryId,
            rootEntityType: enumDocumentRootEntityType.ENTRY_SUMMARY,
            relatedEntityId: entryId,
            relatedEntityType: enumDocumentRelatedEntityType.ENTRY_SUMMARY,
          },
          true,
        );
      }),
    );
  }

  public copyCompanyPoaToCbpRequest(): Observable<DocumentModel[]> {
    return this.findCompanyDocument(enumDocumentType.POWER_OF_ATTORNEY).pipe(
      switchMap((poaDocument: DocumentModel) => {
        if (!poaDocument || poaDocument.state !== enumDocumentState.UPLOADED) {
          return of(null);
        }
        return this.documentsRepository.copy(
          poaDocument.id,
          {
            ...poaDocument,
            relatedEntityId: this.companiesService.getCompany().id,
            relatedEntityType: enumDocumentRelatedEntityType.COMPANY,
            rootEntityId: this.companiesService.getCompany().id,
            rootEntityType: enumDocumentRootEntityType.COMPANY,
            type: enumDocumentType.CBP_ASSIGNED_NUMBER_POWER_OF_ATTORNEY,
          },
          true,
        );
      }),
    );
  }

  public copyCompanyPunToEntry(entryId: number): Observable<DocumentModel[]> {
    return this.findCompanyDocument(enumDocumentType.ACH_APPROVAL).pipe(
      switchMap((punDocument: DocumentModel) => {
        if (!punDocument || punDocument.state !== enumDocumentState.UPLOADED) {
          return of(null);
        }
        return this.documentsRepository.copy(
          punDocument.id,
          {
            ...punDocument,
            rootEntityId: entryId,
            rootEntityType: enumDocumentRootEntityType.ENTRY_SUMMARY,
            relatedEntityId: entryId,
            relatedEntityType: enumDocumentRelatedEntityType.ENTRY_SUMMARY,
          },
          true,
        );
      }),
    );
  }

  public copyEntryPoaToEntry(_parentEntryId: number, entryId: number): Observable<DocumentModel[]> {
    // POA on a duplicated ES must always come from the company's current POA, not the source ES's
    // copy, because broker migration (e.g. LHCB) invalidates POAs at the company level while paid
    // ES retain their stale POA copies. See #13067.
    return this.copyCompanyPoaToEntry(entryId);
  }

  public copyEntryPunToEntry(parentEntryId: number, entryId: number): Observable<DocumentModel[]> {
    return this.getEntryDocuments(parentEntryId, false).pipe(
      map((docs: DocumentModel[]) => this.findDocument(docs, enumDocumentType.ACH_APPROVAL)),
      switchMap((parentPoaDocument: DocumentModel) => {
        if (parentPoaDocument) {
          return this.copyDocToEntry(entryId, parentPoaDocument.id, parentPoaDocument.fileName, enumDocumentType.ACH_APPROVAL);
        } else {
          return this.copyCompanyPunToEntry(entryId);
        }
      }),
    );
  }

  public copyDocToEntry(entryId: number, documentId: number, fileName: string, docType: enumDocumentType): Observable<never> {
    return this.documentsRepository.copy(
      documentId,
      {
        fileName: fileName,
        rootEntityId: entryId,
        rootEntityType: enumDocumentRootEntityType.ENTRY_SUMMARY,
        relatedEntityId: entryId,
        relatedEntityType: enumDocumentRelatedEntityType.ENTRY_SUMMARY,
        type: docType,
      },
      false,
    );
  }

  public copyInvoiceDocToEntry(entryId: number, invoiceId: number, documentId: number, fileName: string): Observable<DocumentModel> {
    return this.documentsRepository.copy(
      documentId,
      {
        fileName: fileName,
        rootEntityId: entryId,
        rootEntityType: enumDocumentRootEntityType.ENTRY_SUMMARY,
        relatedEntityId: invoiceId,
        relatedEntityType: enumDocumentRelatedEntityType.INVOICE,
        type: enumDocumentType.INVOICE,
      },
      false,
    );
  }

  public getEntryDocuments(entryId: number, isShowLoader: boolean): Observable<DocumentModel[]> {
    return this.documentsRepository.getESDocuments(entryId, isShowLoader);
  }

  public getIsfDocuments(isfId: number, isShowLoader: boolean): Observable<DocumentModel[]> {
    return this.documentsRepository.getIsfDocuments(isfId, isShowLoader);
  }

  public getCompanyDocuments(companyId: number, isShowLoader: boolean): Observable<DocumentModel[]> {
    return this.documentsRepository.getCompanyDocuments(companyId, isShowLoader);
  }

  public getLCLDocuments(shipmentId: number, isShowLoader: boolean): Observable<DocumentModel[]> {
    return this.documentsRepository.getLCLDocuments(shipmentId, isShowLoader);
  }

  public getManagementDocuments(
    params: ManagementDocumentSearchModel,
    isShowLoader: boolean,
  ): Observable<PaginatedResultModel<ManagementDocumentModel>> {
    return this.documentsManagementService.search(params, isShowLoader);
  }

  public findDocument(docs: DocumentModel[], docType: enumDocumentType): DocumentModel {
    return docs.find((doc: DocumentModel) => doc.type === docType);
  }

  public getArchive(referenceNumber: string, documentsIds: number[]): Observable<Blob> {
    return this.documentsRepository.getArchive(referenceNumber, documentsIds);
  }

  public getManagementArchive(referenceNumber: string, documentsIds: number[]): Observable<Blob> {
    return this.documentsManagementService.getArchive(referenceNumber, documentsIds);
  }

  public downloadBlobFile(blob: Blob, filename: string): void {
    return this.documentsRepository.downloadBlobFile(blob, filename);
  }

  public getDownloadLink(id: number, isShowLoader: boolean = true): Observable<{ downloadUrl: string }> {
    return this.documentsRepository.getDownloadLink(id, isShowLoader);
  }

  public formatZipFileName(referenceNumber: string): string {
    return `${referenceNumber}-${new Date().toLocaleDateString('en-US').replace(/\//g, '-')}`;
  }

  public uploadCustomsImportFile(companyId: number, file: File, isShowLoader: boolean = false): Observable<never> {
    return this.documentsRepository.uploadCustomsImportFile(companyId, file, isShowLoader);
  }

  public getDocumentState(docId: number, isShowLoader: boolean = false): Observable<enumDocumentState> {
    return this.documentsRepository.getDocumentState(docId, isShowLoader);
  }

  public checkImageFileExtension(name: string): boolean {
    return [enumFileExtension.GIF, enumFileExtension.PNG, enumFileExtension.JPG, enumFileExtension.JPEG].some((ext: enumFileExtension) =>
      name.includes(`.${ext}`),
    );
  }

  public findCompanyDocument(docType: enumDocumentType): Observable<DocumentModel> {
    const company: CompanyModel = this.companiesService.getCompany();
    if (!company) {
      return of(null);
    }
    const documents$ = this.documentsRepository.getCompanyDocuments(company.id, true).pipe(map((docs: DocumentModel[]) => docs || []));

    if (docType !== enumDocumentType.POWER_OF_ATTORNEY) {
      return documents$.pipe(map((docs: DocumentModel[]) => this.findDocument(docs, docType)));
    }

    return forkJoin({
      documents: documents$,
      digitalSigns: this.digitalSignsRepository.list(company.id).pipe(
        map((items) =>
          (items || []).filter((item) => item.state === enumDigitalSignState.Completed && !!item.documentId).sort((a, b) => b.id - a.id),
        ),
        catchError(() => of([])),
      ),
    }).pipe(
      map(({ documents, digitalSigns }) => {
        for (const digitalSign of digitalSigns) {
          const matchedDocument = documents.find(
            (doc: DocumentModel) => doc.id === digitalSign.documentId && doc.type === enumDocumentType.POWER_OF_ATTORNEY,
          );

          if (matchedDocument) {
            return matchedDocument;
          }
        }

        return this.findDocument(documents, docType);
      }),
    );
  }

  public hasCompanyPoa(companyId?: number): Observable<boolean> {
    const id = companyId || this.companiesService.getCompany()?.id;
    if (!id) {
      return of(false);
    }

    return this.findCompanyDocument(enumDocumentType.POWER_OF_ATTORNEY).pipe(
      map((doc: DocumentModel) => !!doc && doc.state === enumDocumentState.UPLOADED),
    );
  }

  // Returns true (proceed without prompting) when: the company has a signed POA, or LHCB
  // migration is active/pending (bypass), or no company is selected (admin/monitoring views).
  // Returns false when the caller should show the POA-required modal. See #13079.
  public canActWithoutPoaPrompt$(): Observable<boolean> {
    if (!this.companiesService.getCompany()?.id) {
      return of(true);
    }

    return combineLatest([
      this.hasCompanyPoa().pipe(
        take(1),
        catchError(() => of(false)),
      ),
      this.lhcbMigrationPopupService.migrationPopupActiveOrPending$.pipe(take(1)),
    ]).pipe(map(([hasPoa, migrationPending]: [boolean, boolean]) => hasPoa || migrationPending));
  }
}
